<template>
    <!-- メッセージダイアログ -->
    <MessageDialog id="modalMessageMainView" :messageText="messageHTML"></MessageDialog>

    <template v-if="AUTH_RESULT_UNDEFINED == nAuthenticate">
        <!-- 認証中 -->
        <div class="d-flex flex-grow-1 flex-column align-items-center justify-content-center">
            <span class="scv-text-loading scv-text-default-color-main-FRONTENDEX-MOBILE-APP">
                読み込み中です…
            </span>
        </div>
    </template>
    <template v-else-if="AUTH_RESULT_WAIT_FEEDBACK == nAuthenticate">
        <!-- 認証待ち -->
        <AppCatalogWaitFeedbackView @onTimer="Authenticate(true)" ref="waitFeedbackView"></AppCatalogWaitFeedbackView>
    </template>
    <template v-else-if="AUTH_RESULT_SUCCESS == nAuthenticate">
        <!-- 認証成功 -->
        <AppCatalogListView @onDetailDisplay="onDetailDisplay" @onInstallUpdate="onInstallUpdate" v-bind:class="{ 'd-none': dispDetailDialog }"></AppCatalogListView>
        <template v-if="dispDetailDialog">
            <AppCatalogDetailView :appInfo="appDetailInfo" @onInstallUpdate="onInstallUpdate" @onReturnBtn="onReturnBtn"></AppCatalogDetailView>
        </template>
        <!-- インストール確認ダイアログ -->
        <ConfirmDialog id="modalConfirmInstall" :confirmMessage="messageConfirmHTML" @onBtn1="onExecInstallUpdate" :btn1="confirmBtn1"></ConfirmDialog>
    </template>
    <template v-else>
        <!-- 認証失敗は真っ白画面 -->
    </template>
</template>

<script lang="ts">
    import { Options, Vue } from 'vue-class-component';
    import AppCatalogListView from '@/views/AppCatalogListView.vue';
    import AppCatalogDetailView from '@/views/AppCatalogDetailView.vue';
    import AppCatalogWaitFeedbackView from '@/views/AppCatalogWaitFeedbackView.vue'
    import { AppInfo } from '@/models/AppCatalog/AppInfo';
    import HttpRequestUtil from '@/util/HttpRequestUtil';
    import ConfirmDialog from '@/components/MessageBox/ConfirmDialog.vue';
    import MessageDialog from '@/components/MessageBox/MessageDialog.vue';
    import "@/assets/css/MobileApp/control.css";
    import "@/assets/css/MobileApp/text_color.css";
    import { BootstrapUtil } from 'lib-vue-v2/util';

    declare const app_version: string;

    /// <summary>
    /// 署名検証結果
    /// ※AppContololler.cs 参照
    /// </summary>
    enum AUTH_RESULT {
        SUCCESS,            // 署名検証成功
        FAILURE,            // 署名検証失敗
        WAIT_FEEDBACK,      // Feedback情報取得待ち
        NOT_FOUND,          // 資産情報削除(判別GUIDに該当する資産情報なし)
        SYSTEM_ERROR,       // システムエラー
        
        UNDEFINED = -1,     // 未定義（UI専用）
    }

    @Options({
        components: {
            AppCatalogListView,
            AppCatalogDetailView,
            AppCatalogWaitFeedbackView,
            ConfirmDialog,
            MessageDialog
        }
    })

    export default class AppCatalogMainView extends Vue {
        private dispDetailDialog = false;
        private appDetailInfo = new AppInfo();  // 詳細表示内容（props用）
        private appInstallUpdate = new AppInfo();  // インストールアップデート対象
        private nAuthenticate = AUTH_RESULT.UNDEFINED;
        private messageConfirmHTML = "";
        private confirmBtn1 = "";
        private messageHTML = "";

        // htmlではenum定義を直接使えないので、こちらでも定義しておく
        private readonly AUTH_RESULT_SUCCESS = AUTH_RESULT.SUCCESS;
        private readonly AUTH_RESULT_WAIT_FEEDBACK = AUTH_RESULT.WAIT_FEEDBACK;
        private readonly AUTH_RESULT_UNDEFINED = AUTH_RESULT.UNDEFINED;

        // ビューでアプリカタログの署名検証を行わないアプリバージョン
        private readonly NOT_AUTHENTICATION_APP_VERSION = 1.1;

        /**
         * マウント後の処理
         */
        public async mounted(): Promise<void> {
            // アプリバージョンのピリオド上位2区切り分を抜き出す
            const judgeAppVersion = this.convertStringToFloat(app_version);

            // ミドルウェアで署名検証を行うアプリバージョン以降なら、ここで署名検証は行わない
            if (this.NOT_AUTHENTICATION_APP_VERSION <= judgeAppVersion) {
                // ミドルウェアで署名検証済みなので、ここでは署名検証を行わず、署名検証成功値を設定する
                this.nAuthenticate = AUTH_RESULT.SUCCESS;
            }
            else {
                // 署名検証を行う。最新バージョンではここを通る事はない。これは過去バージョンのアプリから要求があった場合の為に残している処理
                await this.Authenticate(false);
            }
        }

        /**
         * 認証処理が押下
         * [in] waitFeedback : Feedback待ちかどうか（Feedback待ちなら完了を待ち画面に通知）
         */
        private async Authenticate(waitFeedback: boolean): Promise<void> {
            const res = await HttpRequestUtil.get(
                "/App/Authenticate"
            );

            this.messageHTML = "";

            // レスポンスが取れないときはネットワークエラー
            if (undefined == res || undefined == res.status) {
                this.nAuthenticate = AUTH_RESULT.SYSTEM_ERROR;
                this.messageHTML = "インターネットに接続されていません。<br />モバイルデータ通信またはWi-Fiが有効になっているか確認してください。";
                BootstrapUtil.showModal('modalMessageMainView');
                return;
            }
            // HTTPエラー
            else if (res.status != 200) {
                this.nAuthenticate = AUTH_RESULT.SYSTEM_ERROR;
                this.messageHTML = "アプリカタログの表示に失敗しました。<br />（エラーコード：" + res.status + "）";
                BootstrapUtil.showModal('modalMessageMainView');
                return;
            }
            // サーバーエラー
            else if (undefined == res.data ||
                (AUTH_RESULT.WAIT_FEEDBACK != res.data && AUTH_RESULT.SUCCESS != res.data)) {
                if (undefined != res.data) {
                    this.nAuthenticate = res.data;
                }
                else {
                    this.nAuthenticate = AUTH_RESULT.SYSTEM_ERROR;
                }
                this.messageHTML = "アプリカタログの表示に失敗しました。<br />（エラーコード：500）";
                BootstrapUtil.showModal('modalMessageMainView');
                return;
            }

            if (waitFeedback && AUTH_RESULT.WAIT_FEEDBACK == res.data) {
                // Feedback待ちが継続
                /* eslint @typescript-eslint/no-explicit-any: 0 */
                const refObj: any = this.$refs;
                refObj.waitFeedbackView.TimerSet();
            }

            this.nAuthenticate = res.data;
        }

        /**
         * 「詳細表示」ボタンが押下
         */
        private onDetailDisplay(app: AppInfo): void {
            this.dispDetailDialog = true;
            this.appDetailInfo = app;   // 詳細表示内容をセットしておく
        }

        /**
         * ヘッダーの「戻る」ボタンが押下
         */
        private onReturnBtn(): void {
            this.dispDetailDialog = false;
            this.appDetailInfo = new AppInfo(); // 詳細表示内容は初期化しておく
        }

        /**
         * 「アップデート」ボタン「インストール」ボタンが押下
         */
        private async onInstallUpdate(app: AppInfo): Promise<void> {
            let messege = "";
            if (this.isInstallUpdate(app)) {
                // インストール
                this.messageConfirmHTML = "「" + app.trackName + "」のインストールを行います。<br />よろしいですか？";
                this.confirmBtn1 = "インストール開始";
            }
            else {
                // アップデート
                this.messageConfirmHTML = "「" + app.trackName + "」のアップデートを行います。<br />よろしいですか？";
                this.confirmBtn1 = "アップデート開始";
            }

            // 確認メッセージ
            this.appInstallUpdate = app;
            BootstrapUtil.showModal('modalConfirmInstall');
        }

        /**
         * 「アップデート」ボタン「インストール」ボタンの実行処理
         */
        private async onExecInstallUpdate(): Promise<void> {
            var bodyData = {
                settingID: this.appInstallUpdate.settingID,
            };

            const res = await HttpRequestUtil.post(
                "/App/Execute_InstallUpdate",
                bodyData
            );

            this.messageHTML = "";

            // レスポンスが取れないときはネットワークエラー
            if (undefined == res || undefined == res.status) {
                this.messageHTML = "インターネットに接続されていません。<br />モバイルデータ通信またはWi-Fiが有効になっているか確認してください。";
            }
            else if (res.status != 200) {
                // 失敗
                if (this.isInstallUpdate(this.appInstallUpdate)) {                    
                    // インストール
                    this.messageHTML = "インストール開始に失敗しました。<br />（エラーコード：" + res.status + "）";
                }
                else {
                    // アップデート
                    this.messageHTML = "アップデート開始に失敗しました。<br />（エラーコード：" + res.status + "）";
                }
            }

            if (0 != this.messageHTML.length) {
                // エラーあり
                BootstrapUtil.showModal('modalMessageMainView');
                this.appInstallUpdate = new AppInfo();
                return;
            }

            // UI上でだけ、インストール・アップデートボタンを押せなくする
            this.appInstallUpdate.isRequestInstallUpdate = true;

            this.appInstallUpdate = new AppInfo();
        }

        /**
         * 「アップデート」「インストール」判定
         * @return true : インストール    false : アップデート
         */
        private isInstallUpdate(app: AppInfo): boolean {
            if (!app.isInstalled) {
                return true;
            }
            else {
                // インストール済みならアップデート
                return false;
            }
        }

        /**
         * アプリバージョン(**.**.***)からピリオド区切りで先頭2区切り分を浮動小数点値で返す
         * [in] appVersion : アプリバージョン(**.**.***形式)
         * @return ピリオド区切りで先頭2区切り分を返す
         */
        private convertStringToFloat(inputString: string): number {
            // アプリバージョンの先頭から最後のピリオドまでを抜き出す
            const lastDotPosition = inputString.lastIndexOf('.');
            const truncatedString = inputString.substring(0, lastDotPosition);

            // 浮動小数点値に変換
            const floatValue = parseFloat(truncatedString);
            if (isNaN(floatValue)) {
                return 0;
            }
            return floatValue;
        }
    }
</script>

<style scoped>
    .scv-text-loading {
        font-size: 13px;
    }
</style>